import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import { UHF } from '@airasia-common/libraries/constants';
import { useRouteMatch } from 'react-router-dom';
import { func, object, string, array, bool } from 'prop-types';
import config from '@airasia-common/libraries/config';
import Wrapper from '../Wrapper';
import { ROUTE, DEFAULT_LOCALE } from '../../utils/constants';
import FallbackLanguageMessage from '../FallbackLanguageMessage';
import FallbackCurrencyMessage from '../FallbackCurrencyMessage';

const FALLBACK_LANGUAGE_MESSAGE_KEY =
  'aa_header_show_fallback_language_message';

const AA_TOKENS = 'aa_tokens';

const env =
  config.REACT_APP_UHF_ENV === 'production'
    ? UHF.PRODUCTION_ENV
    : UHF.STAGE_ENV;

const Header = ({
  isInit,
  language,
  currency,
  selectedCurrency,
  updateCurrency,
  setUserProfile,
  supportedLanguages,
  currencyRoute = [],
  currencyBlacklistRoute = [],
  languageRoute = [],
  loginRoute = [],
  languageParamName = 'language',
  isSupportedMultiLanguages,
  isSupportedMultiCurrencies,
  credential,
}) => {
  const [isLoaded, setLoaded] = useState(false);
  const currencyMatch = useRouteMatch(currencyRoute, {
    exact: true,
  });
  const currencyBlacklistMatch = useRouteMatch(currencyBlacklistRoute, {
    exact: true,
  });
  const languageMatch = useRouteMatch(languageRoute, {
    exact: true,
  });
  const loginMatch = useRouteMatch(loginRoute, {
    exact: true,
  });

  useEffect(() => {
    loadStyle(UHF.FONT_URL);
    loadStyle(UHF.STYLE_URL);
    loadScript();
  }, []);

  useEffect(() => {
    if (isLoaded) {
      const isCurrencyRoute =
        currencyMatch &&
        currencyMatch.isExact &&
        !(currencyBlacklistMatch && currencyBlacklistMatch.isExact);
      const isLanguageRoute = languageMatch && languageMatch.isExact;
      const isLoginRoute = loginMatch && loginMatch.isExact;
      const setting = {
        ...UHF.SETTINGS,
        currencySection: isCurrencyRoute && isSupportedMultiCurrencies,
        languageSection: isLanguageRoute && isSupportedMultiLanguages,
        loginSection: isLoginRoute,
      };

      window.setUhfInputs({ setting });
    }
  }, [
    languageMatch,
    isLoaded,
    loginMatch,
    isSupportedMultiLanguages,
    isSupportedMultiCurrencies,
    currencyMatch,
    currencyBlacklistMatch,
  ]);

  const loadScript = () => {
    const script = document.createElement('script');
    script.src = UHF.SCRIPT_URL;
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
  };

  const loadStyle = href => {
    const link = document.createElement('link');
    link.href = href;
    link.rel = 'stylesheet';
    document.body.appendChild(link);
  };

  useEffect(() => {
    const handleLocale = ({ detail }) => {
      let newLocale = detail;
      if (
        supportedLanguages.length > 0 &&
        !supportedLanguages.includes(detail)
      ) {
        if (isSupportedMultiLanguages) {
          Cookies.set(FALLBACK_LANGUAGE_MESSAGE_KEY, true);
        }

        newLocale = UHF.DEFAULT_LANGUAGE;
      }
      const { pathname, search } = window.location;

      Cookies.set(DEFAULT_LOCALE, newLocale);
      const currentSearch = queryString.parse(search);
      let newSearch = {
        ...currentSearch,
        [languageParamName]: newLocale,
      };
      window.history.replaceState(
        window.history.state,
        '',
        `${pathname}?${queryString.stringify(newSearch)}`
      );
      window.location.reload();
    };

    if (isLoaded) {
      isInit && window.setUhfInputs({ lang: language });
      window.addEventListener('uhfLocaleChange', handleLocale);
    }

    return () => {
      window.removeEventListener('uhfLocaleChange', handleLocale);
    };
  }, [
    isLoaded,
    supportedLanguages,
    language,
    isSupportedMultiLanguages,
    isInit,
    languageParamName,
  ]);

  const shouldUpdateUserProfile = (
    currentProfile = {},
    updatedProfile = {}
  ) => {
    if (currentProfile.status !== updatedProfile.status) {
      return true;
    }
    const currentSSO = currentProfile.ssoData || {};
    const updatedSSO = updatedProfile.ssoData || {};
    const currentSSOKeys = Object.keys(currentSSO);
    const updatedSSOKeys = Object.keys(updatedSSO);
    if (currentSSOKeys.length !== updatedSSOKeys.length) {
      return true;
    }
    currentSSOKeys.forEach(key => {
      if (
        typeof currentSSO[key] !== 'object' &&
        currentSSO[key] !== updatedSSO[key]
      ) {
        return true;
      }
    });
    return false;
  };

  useEffect(() => {
    const handleLoginChange = ({ detail }) => {
      if (shouldUpdateUserProfile(credential, detail)) {
        const aaTokens = localStorage.getItem(AA_TOKENS);
        setUserProfile({
          ...detail,
          authInfo: aaTokens ? JSON.parse(aaTokens) : null,
        });
      }

      //TODO - UHF-1.5.4 - Can not SSO when windown.location.href has more than 1 parameters.
      //Reason - UHF-1.5.4 - Do not encodeURLComponent redirectURL before call SSO service. RedirectURL is modified after SSO successfully,
      //so SSO popup window can not close because do not match condition (window.location.href.indexOf(window.opener.location.href) > -1)
      //These below code try to make window.location.href = (window.opener.location.href + sso params)

      const { search } = window.location;
      const currentSearch = queryString.parse(
        decodeURIComponent(search).replace('?accessToken=', '&accessToken=')
      );
      const currentParameterNames = Object.keys(currentSearch);
      if (
        window.opener &&
        currentParameterNames.includes('accessToken') &&
        currentParameterNames.includes('userId')
      ) {
        const { search: openerSearch } = window.opener.location;
        const openerParameterNames = Object.keys(
          queryString.parse(openerSearch)
        );
        const ssoParams = currentParameterNames
          .filter(key => !openerParameterNames.includes(key))
          .reduce((sso, key) => {
            sso[key] = currentSearch[key];
            return sso;
          }, {});

        window.location.href = `${
          window.opener.location.href
        }&${queryString.stringify(ssoParams)}`;
      }
    };

    if (isLoaded) {
      window.addEventListener('uhfLoginChange', handleLoginChange);
    }

    return () => {
      window.removeEventListener('uhfLoginChange', handleLoginChange);
    };
  }, [isLoaded, setUserProfile, credential]);

  useEffect(() => {
    const handleCurrency = ({ detail }) => {
      updateCurrency({ currency: detail });
    };

    if (isLoaded) {
      window.setUhfInputs({ curr: currency });
      window.addEventListener('uhfCurrencyChange', handleCurrency);
    }

    return () => {
      window.removeEventListener('uhfCurrencyChange', handleCurrency);
    };
  }, [isLoaded, currency, updateCurrency, selectedCurrency]);

  let showFallbackLanguageMessage = false;

  if (isSupportedMultiLanguages) {
    showFallbackLanguageMessage = Boolean(
      Cookies.get(FALLBACK_LANGUAGE_MESSAGE_KEY)
    );
    Cookies.remove(FALLBACK_LANGUAGE_MESSAGE_KEY);
  }

  return (
    <Wrapper>
      {isSupportedMultiLanguages && (
        <FallbackLanguageMessage
          language={language}
          isShow={showFallbackLanguageMessage}
        />
      )}
      {isSupportedMultiCurrencies && (
        <FallbackCurrencyMessage
          currency={currency}
          selectedCurrency={selectedCurrency}
        />
      )}
      <Wrapper
        display={[
          window.location.pathname === ROUTE.ROOT ? 'block' : 'none',
          'block',
        ]}
      >
        <aa-header
          env={env}
          setting={JSON.stringify(UHF.SETTINGS)}
          lang={language}
        />
      </Wrapper>
    </Wrapper>
  );
};

Header.propTypes = {
  isInit: bool,
  language: string,
  currency: string,
  selectedCurrency: object,
  updateCurrency: func,
  setUserProfile: func,
  updateLanguage: func,
  supportedLanguages: array,
  currencyRoute: array,
  currencyBlacklistRoute: array,
  languageRoute: array,
  loginRoute: array,
  languageParamName: string,
  isSupportedMultiLanguages: bool,
  isSupportedMultiCurrencies: bool,
  credential: object,
};

export default Header;
